import React, { useEffect, useState } from 'react';
import { Toolbar, Typography, AppBar, Stack, Drawer, Button, Collapse, Box, Accordion as MuiAccordion, FormGroup, FormControlLabel, Switch, AccordionSummary, AccordionDetails, IconButton, Skeleton, Checkbox, Alert, Divider, CircularProgress } from '@mui/material';
import { ExpandMore, Close } from '@mui/icons-material';
import { format, formatISO } from 'date-fns';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import usePrevious from '../hooks/usePrevious';
import NavigationItem from './NavigationItem';
import ConfirmRemoveContactDialog from './dialogs/ConfirmRemoveContactDialog';
import ConfirmUnsubscribeContactDialog from './dialogs/ConfirmUnsubscribeContactDialog';
import ConfirmResubscribeContactDialog from './dialogs/ConfirmResubscribeContactDialog';
import ConfirmSuppressContactDialog from './dialogs/ConfirmSuppressContactDialog';
import ConfirmUnsuppressContactDialog from './dialogs/ConfirmUnsuppressContactDialog';
import UnsavedChangesDialog from './dialogs/UnsavedChangesDialog';
import SendHistory from './contact/SendHistory';
import GdprActions from './contact/GdprActions';
import CustomFields from './contact/CustomFields';
import Details from './contact/Details';
import CampaignContactData from './contact/CampaignContactData';

const Accordion = styled(MuiAccordion)(({ theme }) => ({
    '&.MuiAccordion-root.Mui-disabled': {
        backgroundColor: theme.palette.background.paper
    }
}));

const Status = ({ isUnsubscribed, hasBounced, suppressedUntil, isDeleted }) => {
    const suppressedUntilFormatted = format(new Date(suppressedUntil), 'MMMM dd, yyyy');

    return (
        <>
            <Alert
                severity={(isUnsubscribed || isDeleted) ? 'error' : hasBounced ? 'warning' : 'info'}
            >
                {isUnsubscribed ? (
                    'Unsubscribed: Does not receive any campaigns from your account'
                ) : isDeleted ? (
                    'Contact has been deleted from your account'
                ) : hasBounced ? (
                    'Soft Bounce: Contact may not receive campaigns from your account'
                ) : suppressedUntil !== null ? (
                    <>
                        Suppressed: Will not receive any campaigns from your account until <strong>{suppressedUntilFormatted}</strong>
                    </>
                ) : (
                    'Contact is not currently suppressed'
                )}
            </Alert>
            <Divider />
        </>
    );
};

const ListItems = ({ items, type, selectedItems, showCount = true, onSetSelectedItem = null, showCreatedDate = false, contactGroupAddedDates }) => {
    if (items && items.length > 0) {
        return items.filter(item => item.groupType !== 'archive')
            .map(item => {
                const selected = selectedItems.includes(item.id);

                const handleSelectItem = () => {
                    onSetSelectedItem && onSetSelectedItem(item);
                };

                return (
                    <NavigationItem
                        key={item.id}
                        item={item}
                        type={type}
                        selected={selected}
                        onSetSelectedItem={handleSelectItem}
                        showCount={showCount}
                        showCreatedDate={showCreatedDate}
                        contactGroupAddedDates={contactGroupAddedDates}
                        action={(
                            <Checkbox
                                edge="end"
                                checked={selected}
                                tabIndex={-1}
                                disableRipple
                                onChange={handleSelectItem}
                                disabled={!onSetSelectedItem}
                            />
                        )}
                    />
                );
            });
    }

    return (
        <Typography
            variant="body2"
            sx={{ padding: 2, textAlign: 'center' }}
        >
            No {type}s found
        </Typography>
    );
};

const defaultPanel = 'details';

const Contact = ({ open, onMount, onClose, id, onRefresh = null, campaignId = null }) => {
    const { handleGet, handlePut, handleDelete } = useApi();
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const [dialog, setDialog] = useState(null);
    const [manageGroups, setManageGroups] = useState(false);
    const [expanded, setExpanded] = useState(defaultPanel);
    const [contact, setContact] = useState(null);
    const [unsavedFields, setUnsavedFields] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [groups, setGroups] = useState(null);
    const [segments, setSegments] = useState(null);
    const [updatedContactGroupIds, setUpdatedContactGroupIds] = useState([]);
    const [unsavedGroups, setUnsavedGroups] = useState(false);
    const [updatedCustomFields, setUpdatedCustomFields] = useState([]);
    const [newCustomFields, setNewCustomFields] = useState([]);
    const [newDetails, setNewDetails] = useState(null);
    const [campaignRecipient, setCampaignRecipient] = useState(null);
    const [contactGroupAddedDates, setContactGroupAddedDates] = useState([]);

    const prevUpdatedContactGroupIds = usePrevious(updatedContactGroupIds);
    const prevUpdatedCustomFields = usePrevious(updatedCustomFields);

    const handleChange = panel => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : 'none');
    };

    const handleFetchContact = async () => {
        const response = await handleGet(`contacts/${id}`);

        if (!response.ok) {
            setIsLoading(false);
            showSnackbar('Contact not found', 'error');
            console.error(response?.error);
            return;
        }

        const contact = await response.json();

        setContact(contact);

        setUpdatedContactGroupIds(contact.groupIds);
        setContactGroupAddedDates(contact.emailGroups);
        setIsLoading(false);
    };

    const handleFetchCampaignRecipient = async () => {
        const response = await handleGet(`reports/${campaignId}/subscribers/${id}`);

        if (!response.ok) {
            showSnackbar('Campaign information not found', 'error');
            console.error(response?.error);
            return;
        }

        setCampaignRecipient(await response.json());
    };

    const handleClose = () => {
        setDialog(null);
        onClose();
        setContact(null);
    };

    const maybeRefresh = () => {
        onRefresh && onRefresh();
    };

    const handleRemoveContact = async () => {
        const response = await handleDelete(`contacts/${contact.id}`);

        if (!response.ok) {
            showSnackbar('Contact not found', 'error');
            console.error(response?.error);
            return;
        }

        showSnackbar('Contact deleted', 'success');

        handleClose();
        maybeRefresh();
    };

    const handleUpdateContact = async newContactDetails => {
        setIsLoading(true);

        const newContact = Object.assign(contact, newContactDetails);

        const response = await handlePut(`contacts/${id}`, newContact);

        if (!response.ok) {
            const statusCode = response?.status;
            let errorMessage = 'Error updating contact';
            const json = await response.json();

            switch (statusCode) {
                case 404:
                    errorMessage = 'Contact not found';
                    break;
                case 409:
                    errorMessage = 'Email Address already exists';
                    break;
                case 400:
                    errorMessage = json.message;
                    break;
                default:
                    break;
            }

            showSnackbar(errorMessage, 'error');

            console.error(response?.error);
            setIsLoading(false);
            return;
        }

        if (contact && updatedContactGroupIds) {
            setUpdatedCustomFields([]);
            setNewDetails(null);
        }

        showSnackbar('Contact updated', 'success');
    };

    const handleFetchGroups = async () => {
        const response = await handleGet('groups');

        if (!response.ok) {
            showSnackbar('Groups not found', 'error');
            console.error(response?.error);
            return;
        }

        const groups = await response.json();

        setGroups(groups);
    };

    const handleUpdateContactGroupsDetailsCustomFields = async () => {
        const newContactData = {
            ...(updatedContactGroupIds && { groupIds: updatedContactGroupIds }),
            ...(newDetails && newDetails),
            customFields: newCustomFields?.length > 0 ? newCustomFields : contact.customFields
        };

        await handleUpdateContact(newContactData);

        await handleFetchGroups();
        handleFetchContact();
        maybeRefresh();
    };

    const handleUnsubscribeContact = async isUnsubscribed => {
        const newContactData = {
            isUnsubscribed,
            ...(newDetails && newDetails),
            ...(updatedContactGroupIds && { groupIds: updatedContactGroupIds }),
            customFields: newCustomFields?.length > 0 ? newCustomFields : contact.customFields
        };

        await handleUpdateContact(newContactData);

        handleClose();
        maybeRefresh();
    };

    const handleSuppressContact = async suppressUntilDate => {

        const newContactData = {
            ...newDetails,
            suppressUntilDate: suppressUntilDate ? formatISO(suppressUntilDate) : null,
            ...(updatedContactGroupIds && { groupIds: updatedContactGroupIds }),
            customFields: newCustomFields?.length > 0 ? newCustomFields : contact.customFields
        };

        await handleUpdateContact(newContactData);

        handleFetchContact();
        maybeRefresh();
    };

    const handleFetchSegments = async () => {
        const response = await handleGet('segments');

        if (!response.ok) {
            showSnackbar('Segments not found', 'error');
            console.error(response?.error);
            return;
        }

        const segments = await response.json();

        setSegments(segments);
    };

    const handleClickClose = () => {
        if (isLoading) {
            return;
        }

        if (unsavedFields || unsavedGroups) {
            setDialog(
                <UnsavedChangesDialog
                    onClose={() => setDialog(null)}
                    onDiscard={handleClose}
                />
            );

            return;
        }

        handleClose();
    };

    const handleToggleGroup = item => {
        const currentlySelected = updatedContactGroupIds.includes(item.id);

        if (currentlySelected) {
            setUpdatedContactGroupIds(updatedContactGroupIds.filter(id => id !== item.id));
        }
        else {
            setUpdatedContactGroupIds([...updatedContactGroupIds, item.id]);
        }
    };

    const handleOpenSubscribeDialog = () => {
        if (contact.isUnsubscribed || contact.hasBounced) {
            setDialog(
                <ConfirmResubscribeContactDialog
                    onClose={() => setDialog(null)}
                    onConfirm={() => handleUnsubscribeContact(false)}
                    emailAddress={contact?.emailAddress}
                />
            );
        }

        if (!contact.isUnsubscribed) {
            setDialog(
                <ConfirmUnsubscribeContactDialog
                    onClose={() => setDialog(null)}
                    onConfirm={() => handleUnsubscribeContact(true)}
                    emailAddress={contact?.emailAddress}
                />
            );
        }
    };

    const handleOpenSuppressionDialog = () => {
        if (contact.isSuppressed) {
            setDialog(
                <ConfirmUnsuppressContactDialog
                    onClose={() => setDialog(null)}
                    onConfirm={() => handleSuppressContact(null)}
                    emailAddress={contact?.emailAddress}
                />
            );
        }
        else {
            setDialog(
                <ConfirmSuppressContactDialog
                    onClose={() => setDialog(null)}
                    onConfirm={handleSuppressContact}
                    emailAddress={contact?.emailAddress}
                />
            );
        }
    };

    const handleToggleManageGroups = e => {
        e.stopPropagation();
        setManageGroups(e.target.checked);
    };

    useEffect(() => {
        if (!contact?.id) {
            return;
        }

        if (expanded === 'groups' && !groups) {
            handleFetchGroups();
        }

        if (expanded === 'segments' && !segments) {
            handleFetchSegments();
        }
    }, [expanded]);

    useEffect(() => {
        if (id) {
            handleFetchContact();
            handleFetchGroups();
        }
        else {
            setExpanded(defaultPanel);
        }
    }, [id]);

    useEffect(() => {
        if (!contact) {
            return;
        }

        if (updatedContactGroupIds !== prevUpdatedContactGroupIds) {
            if (JSON.stringify(updatedContactGroupIds) !== JSON.stringify(contact.groupIds)) {
                setUnsavedGroups(true);
            }
            else {
                setUnsavedGroups(false);
            }
        }
    }, [updatedContactGroupIds]);

    useEffect(() => {
        if (!contact) {
            return;
        }

        if (updatedCustomFields !== prevUpdatedCustomFields) {
            const newCustomFields = contact.customFields.map(field => {
                const updatedField = updatedCustomFields.find(e => e.id === field.id);

                //we need this value even if it's null
                if (updatedField) {
                    return { ...field, value: updatedField.value };
                }

                return field;
            });

            setNewCustomFields(newCustomFields);
        }
    }, [updatedCustomFields]);

    // eslint-disable-next-line no-unused-vars
    const [isValid, setIsValid] = useState(true);
    const [unsavedDetails, setUnsavedDetails] = useState(false);
    const [isReverting, setIsReverting] = useState(false);

    useEffect(() => {
        setUnsavedFields(unsavedDetails);
    }, [unsavedDetails]);

    useEffect(() => {
        if (campaignId) {
            handleFetchCampaignRecipient();
            setExpanded('campaign');
        }
    }, [campaignId]);

    useEffect(() => {
        onMount();
    }, []);

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={handleClickClose}
                variant="temporary"
                ModalProps={{
                    keepMounted: false
                }}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: theme.palette.background.default
                        }
                    }
                }}
            >
                <Toolbar />
                <Box
                    sx={{
                        overflow: 'auto',
                        width: 840
                    }}
                >
                    {contact ? (
                        <>
                            <AppBar
                                position="sticky"
                                color="inherit"
                            >
                                <Toolbar>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6">
                                            {contact.emailAddress}
                                        </Typography>
                                    </Box>
                                    <IconButton onClick={handleClickClose}>
                                        <Close />
                                    </IconButton>
                                </Toolbar>
                            </AppBar>
                            {(contact.isUnsubscribed || contact.hasBounced || contact.isSuppressed || contact.isDeleted) && (
                                <Status
                                    isUnsubscribed={contact.isUnsubscribed}
                                    isDeleted={contact.isDeleted}
                                    hasBounced={contact.hasBounced}
                                    suppressedUntil={contact.suppressUntilDate}
                                />
                            )}
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{
                                    alignItems: 'center',
                                    padding: 2
                                }}
                            >
                                {(!contact.isUnsubscribed && !contact.hasBounced) && (
                                    <Button
                                        onClick={handleOpenSuppressionDialog}
                                        disabled={contact.isDeleted}
                                    >
                                        {contact.isSuppressed ? 'Unsuppress' : 'Suppress'}
                                    </Button>
                                )}
                                {(!contact.isUnsubscribed && !contact.hasBounced) && ( //separated from above to maintain stack
                                    <Button
                                        onClick={() => setDialog(
                                            <ConfirmRemoveContactDialog
                                                onClose={() => setDialog(null)}
                                                onConfirm={handleRemoveContact}
                                                emailAddress={contact?.emailAddress}
                                            />
                                        )}
                                        disabled={contact.isDeleted}
                                    >
                                        Delete
                                    </Button>
                                )}
                                <Button
                                    onClick={handleOpenSubscribeDialog}
                                    disabled={contact.isDeleted}
                                >
                                    {contact.isUnsubscribed ? 'Resubscribe' : 'Unsubscribe'}
                                </Button>
                            </Stack>
                            <Collapse in={unsavedDetails || unsavedFields || unsavedGroups} sx={{ px: 2 }}>
                                <Alert
                                    severity={isValid ? 'info' : 'error'}
                                    action={(
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                onClick={handleUpdateContactGroupsDetailsCustomFields}
                                                size="small"
                                                disabled={
                                                    isLoading &&
                                                    (
                                                        !(unsavedDetails && isValid) ||
                                                        !(unsavedFields && isValid) ||
                                                        !unsavedGroups
                                                    )
                                                }
                                            >
                                                Save changes
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setIsReverting(true);
                                                    setUpdatedContactGroupIds(contact.groupIds);
                                                }}
                                                size="small"
                                                disabled={isLoading}
                                                variant="outlined"
                                            >
                                                Revert
                                            </Button>
                                        </Stack>
                                    )}
                                    sx={{ display: 'flex', padding: 2, marginBottom: 2 }}
                                >
                                    {isLoading ? 'Updating...' : `There are unsaved changes.${isValid ? '' : ' Email Address is invalid, please correct before saving.'}`}
                                </Alert>
                            </Collapse>
                            <Box sx={{ px: 2, pb: 2 }}>
                                <Accordion expanded={expanded === 'details'} onChange={handleChange('details')} disabled={contact.isDeleted}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls="details"
                                        id="details-header"
                                    >
                                        <Typography component="span">Details</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Details
                                            contact={contact}
                                            isLoading={isLoading}
                                            panelOpen={expanded === 'details'}
                                            setNewDetails={setNewDetails}
                                            setUnsavedDetails={setUnsavedDetails}
                                            setIsReverting={setIsReverting}
                                            isReverting={isReverting}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion expanded={expanded === 'custom'} onChange={handleChange('custom')} disabled={contact.customFields.length === 0 || contact.isDeleted}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls="custom"
                                        id="custom-header"
                                    >
                                        <Typography component="span">Custom Fields</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <CustomFields
                                            customFields={contact.customFields}
                                            isLoading={isLoading}
                                            setUnsavedFields={unsavedFields => setUnsavedFields(unsavedFields)}
                                            updatedCustomFields={updatedCustomFields}
                                            setUpdatedCustomFields={updatedCustomFields => setUpdatedCustomFields(updatedCustomFields)}
                                            setIsReverting={setIsReverting}
                                            isReverting={isReverting}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion expanded={expanded === 'groups'} onChange={handleChange('groups')} disabled={contact.isDeleted}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls="groups"
                                        id="groups-header"
                                    >
                                        <Typography component="span">Groups</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <FormGroup sx={{ marginBottom: 1 }}>
                                            <FormControlLabel control={<Switch checked={manageGroups} onChange={handleToggleManageGroups} />} label="Manage Groups" />
                                        </FormGroup>
                                        {manageGroups ? (
                                            <>
                                                <ListItems
                                                    onSetSelectedItem={handleToggleGroup}
                                                    items={groups}
                                                    type="group"
                                                    selectedItems={updatedContactGroupIds}
                                                    showCreatedDate={true}
                                                    contactGroupAddedDates={contactGroupAddedDates}
                                                />
                                            </>
                                        ) : (
                                            <ListItems
                                                items={updatedContactGroupIds ? groups?.filter(e => updatedContactGroupIds.includes(e.id)) : []}
                                                type="group"
                                                selectedItems={updatedContactGroupIds}
                                                showCreatedDate={true}
                                                contactGroupAddedDates={contactGroupAddedDates}
                                            />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion expanded={expanded === 'segments'} onChange={handleChange('segments')} disabled={contact.isDeleted}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls="segments"
                                        id="segments-header"
                                    >
                                        <Typography component="span">Segments</Typography>
                                    </AccordionSummary>
                                    {segments && (
                                        <AccordionDetails>
                                            <ListItems
                                                items={contact.segmentIds ? segments.filter(e => contact.segmentIds.includes(e.id)) : []}
                                                type="segment"
                                                selectedItems={contact.segmentIds}
                                                showCount={false}
                                            />
                                        </AccordionDetails>
                                    )}
                                </Accordion>
                                <Accordion expanded={expanded === 'sendHistory'} onChange={handleChange('sendHistory')}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls="sendHistory"
                                        id="sendHistory-header"
                                    >
                                        <Typography component="span">Send History</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <SendHistory
                                            id={id}
                                            isReport={Boolean(campaignId)}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion expanded={expanded === 'gdpr'} onChange={handleChange('gdpr')} disabled={contact.isDeleted}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls="gdpr"
                                        id="gdpr-header"
                                    >
                                        <Typography component="span">GDPR</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <GdprActions id={id} />
                                    </AccordionDetails>
                                </Accordion>
                                {campaignId && (
                                    <Accordion expanded={expanded === 'campaign'} onChange={handleChange('campaign')}>
                                        <AccordionSummary
                                            expandIcon={campaignRecipient ? <ExpandMore /> : <CircularProgress size={16} color="inherit" />}
                                            aria-controls="campaign"
                                            id="campaign-header"
                                        >
                                            <Typography component="span">Campaign Report</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 0 }}>
                                            {campaignRecipient ? (
                                                <CampaignContactData
                                                    contact={campaignRecipient}
                                                />
                                            ) : <Skeleton height={250} />}
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        </>
                    ) : (
                        <Stack spacing={2} sx={{ padding: 2 }}>
                            <Skeleton variant="text" />
                            <Skeleton variant="rectangular" width="100%" height={campaignId ? 50 : 400} />
                            <Skeleton variant="rectangular" width="100%" height={50} />
                            <Skeleton variant="rectangular" width="100%" height={50} />
                            <Skeleton variant="rectangular" width="100%" height={50} />
                            <Skeleton variant="rectangular" width="100%" height={50} />
                            <Skeleton variant="rectangular" width="100%" height={50} />
                            {campaignId && (
                                <Skeleton variant="rectangular" width="100%" height={330} />
                            )}
                        </Stack>
                    )}
                </Box>
            </Drawer>
            {dialog}
        </>
    );
};

export default Contact;